/**
 * The review's margin beside a draft, as comments sit in Word or Google
 * Docs: each open issue's card level with the paragraph (or table row) it's
 * about, pushed down where cards would overlap, the selected one kept level
 * with its paragraph. Issues that name no paragraph the draft has — a page
 * only — and the closed ones sit at the top.
 *
 * Positions come from the rendered draft (`anchorElement`), measured again
 * whenever the draft or a card changes size.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';

import { Stack, Text } from '@mantine/core';

import type { ReviewIssue } from '@/types/workbench';
import { anchorElement, issueRank, layoutMargin, type MarginItem } from './marginLayout';
import { ClosedToggle } from './ReviewIssues';
import classes from './DraftView.module.css';

const GAP = 8;
/** A card's height before it's measured. */
const UNMEASURED = 96;

export interface MarginPlace {
  cardRef: (el: HTMLDivElement | null) => void;
  style?: CSSProperties;
}

interface ReviewMarginProps {
  issues: ReviewIssue[];
  /** The rendered draft. */
  docRoot: RefObject<HTMLDivElement | null>;
  selected: string | null;
  renderIssue: (issue: ReviewIssue, place: MarginPlace) => ReactNode;
}

interface Placed {
  tops: Map<string, number>;
  apart: string[];
  height: number;
}

function same(a: Placed, b: Placed): boolean {
  if (a.height !== b.height || a.apart.join('|') !== b.apart.join('|')) {
    return false;
  }
  if (a.tops.size !== b.tops.size) {
    return false;
  }
  for (const [id, top] of a.tops) {
    if (b.tops.get(id) !== top) {
      return false;
    }
  }
  return true;
}

export function ReviewMargin({
  issues,
  docRoot,
  selected,
  renderIssue,
}: Readonly<ReviewMarginProps>) {
  const marginRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const cards = useRef(new Map<string, HTMLDivElement>());
  const [showClosed, setShowClosed] = useState(false);
  const [placed, setPlaced] = useState<Placed>({ tops: new Map(), apart: [], height: 0 });

  const open = issues
    .filter((i) => i.status === 'open')
    .sort((a, b) => issueRank(a) - issueRank(b));
  const closed = issues.filter((i) => i.status !== 'open');

  // What the last render showed, for a measure the observers start.
  const openRef = useRef<ReviewIssue[]>([]);
  const selectedRef = useRef<string | null>(null);

  const measure = useCallback(() => {
    const root = docRoot.current;
    const margin = marginRef.current;
    if (!root || !margin) {
      return;
    }
    const base = margin.getBoundingClientRect().top;
    const items: MarginItem[] = [];
    const apart: string[] = [];
    for (const issue of openRef.current) {
      const el = anchorElement(root, issue);
      if (!el) {
        apart.push(issue.id);
        continue;
      }
      items.push({
        id: issue.id,
        anchor: el.getBoundingClientRect().top - base,
        height: cards.current.get(issue.id)?.offsetHeight || UNMEASURED,
      });
    }
    const start = topRef.current?.offsetHeight ? topRef.current.offsetHeight + GAP : 0;
    const tops = layoutMargin(items, selectedRef.current, { gap: GAP, start });
    let height = start;
    for (const item of items) {
      height = Math.max(height, (tops.get(item.id) ?? 0) + item.height);
    }
    const next = { tops, apart, height };
    setPlaced((prev) => (same(prev, next) ? prev : next));
  }, [docRoot]);

  // After every render: the issues, the selection, a card's height or what
  // sits at the top may have changed (a state update only when a place
  // moved).
  useLayoutEffect(() => {
    openRef.current = open;
    selectedRef.current = selected;
    measure();
  });

  // The draft loading, an edit, a narrower window: measured again.
  useEffect(() => {
    const observer = new ResizeObserver(() => measure());
    if (docRoot.current) {
      observer.observe(docRoot.current);
    }
    if (marginRef.current) {
      observer.observe(marginRef.current);
    }
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [docRoot, measure]);

  const cardRef = (id: string) => (el: HTMLDivElement | null) => {
    if (el) {
      cards.current.set(id, el);
    } else {
      cards.current.delete(id);
    }
  };
  const apart = new Set(placed.apart);

  return (
    <div
      ref={marginRef}
      className={classes.margin}
      style={{ minHeight: placed.height }}
      data-testid="review-margin"
    >
      <div ref={topRef}>
        <Stack gap="xs">
          {open.length === 0 && closed.length === 0 ? (
            <Text size="xs" c="dimmed">
              The reviewer hasn&apos;t raised anything on this draft.
            </Text>
          ) : null}
          {open
            .filter((issue) => apart.has(issue.id))
            .map((issue) => renderIssue(issue, { cardRef: cardRef(issue.id) }))}
          <ClosedToggle count={closed.length} shown={showClosed} onToggle={setShowClosed} />
          {showClosed
            ? closed.map((issue) => renderIssue(issue, { cardRef: cardRef(issue.id) }))
            : null}
        </Stack>
      </div>
      {open
        .filter((issue) => !apart.has(issue.id))
        .map((issue) => {
          const top = placed.tops.get(issue.id);
          return renderIssue(issue, {
            cardRef: cardRef(issue.id),
            style: {
              position: 'absolute',
              left: 0,
              right: 0,
              top: top ?? 0,
              visibility: top === undefined ? 'hidden' : undefined,
            },
          });
        })}
    </div>
  );
}
